const STOCK_HOLDING_STATUSES = new Set(['pending', 'approved']);

export const normalizeStatus = (status) => String(status || '').trim().toLowerCase();

export const isStockHoldingStatus = (status) => STOCK_HOLDING_STATUSES.has(normalizeStatus(status));

export const toDateOnly = (rawValue) => {
  if (!rawValue) return null;

  if (typeof rawValue === 'number' && Number.isFinite(rawValue)) {
    const fromNumber = new Date(rawValue);
    if (!Number.isNaN(fromNumber.getTime())) {
      return new Date(fromNumber.getFullYear(), fromNumber.getMonth(), fromNumber.getDate(), 12, 0, 0, 0);
    }
  }

  if (typeof rawValue === 'object' && typeof rawValue.toDate === 'function') {
    const timestampDate = rawValue.toDate();
    if (timestampDate instanceof Date && !Number.isNaN(timestampDate.getTime())) {
      return new Date(
        timestampDate.getFullYear(),
        timestampDate.getMonth(),
        timestampDate.getDate(),
        12, 0, 0, 0
      );
    }
  }

  if (rawValue instanceof Date && !Number.isNaN(rawValue.getTime())) {
    return new Date(rawValue.getFullYear(), rawValue.getMonth(), rawValue.getDate(), 12, 0, 0, 0);
  }

  const textValue = String(rawValue).trim();
  const yyyyMmDd = textValue.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (yyyyMmDd) {
    const [, year, month, day] = yyyyMmDd;
    return new Date(Number(year), Number(month) - 1, Number(day), 12, 0, 0, 0);
  }

  // Support slash-separated formats often seen in legacy docs.
  const slashDate = textValue.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (slashDate) {
    const first = Number(slashDate[1]);
    const second = Number(slashDate[2]);
    const year = Number(slashDate[3]);

    // If the first part is > 12, interpret as DD/MM/YYYY, otherwise MM/DD/YYYY.
    const month = first > 12 ? second : first;
    const day = first > 12 ? first : second;
    if (month >= 1 && month <= 12 && day >= 1 && day <= 31) {
      return new Date(year, month - 1, day, 12, 0, 0, 0);
    }
  }

  const fallback = new Date(textValue);
  if (!Number.isNaN(fallback.getTime())) {
    return new Date(fallback.getFullYear(), fallback.getMonth(), fallback.getDate(), 12, 0, 0, 0);
  }

  return null;
};

export const bookingOverlapsRange = (booking, startDate, endDate) => {
  const bookingStart = toDateOnly(booking?.startDate);
  const bookingEnd = toDateOnly(booking?.endDate);
  const requestedStart = toDateOnly(startDate);
  const requestedEnd = toDateOnly(endDate);

  if (!bookingStart || !bookingEnd || !requestedStart || !requestedEnd) return false;

  // Guard against reversed ranges in stored data or UI edits.
  const normalizedBookingStart = bookingStart <= bookingEnd ? bookingStart : bookingEnd;
  const normalizedBookingEnd = bookingStart <= bookingEnd ? bookingEnd : bookingStart;
  const normalizedRequestedStart = requestedStart <= requestedEnd ? requestedStart : requestedEnd;
  const normalizedRequestedEnd = requestedStart <= requestedEnd ? requestedEnd : requestedStart;

  return normalizedRequestedStart <= normalizedBookingEnd && normalizedRequestedEnd >= normalizedBookingStart;
};

const normalizeEquipmentText = (value) => String(value || '')
  .trim()
  .toLowerCase()
  .replace(/[^a-z0-9]+/g, ' ')
  .replace(/\s+/g, ' ')
  .trim();

const isMatchingEquipment = (bookingItem, equipmentItem) => {
  if (!bookingItem || !equipmentItem) return false;

  const bookingEquipmentId = String(
    bookingItem.equipmentId ??
    bookingItem.id ??
    bookingItem.itemId ??
    ''
  ).trim();
  const targetEquipmentId = String(
    equipmentItem.id ??
    equipmentItem.equipmentId ??
    equipmentItem.itemId ??
    ''
  ).trim();
  if (bookingEquipmentId && targetEquipmentId && bookingEquipmentId === targetEquipmentId) {
    return true;
  }

  const bookingName = normalizeEquipmentText(bookingItem.equipmentName);
  const targetName = normalizeEquipmentText(equipmentItem.name);
  if (!bookingName || !targetName) return false;

  // Accept exact match or semantic near-match when names vary by punctuation/model suffixes.
  return bookingName === targetName || bookingName.includes(targetName) || targetName.includes(bookingName);
};

const getBookedQtyForEquipment = (booking, equipmentItem) => {
  if (!Array.isArray(booking?.items)) return 0;
  const bookingItem = booking.items.find((item) => isMatchingEquipment(item, equipmentItem));
  return Number(
    bookingItem?.quantity ??
    bookingItem?.qty ??
    bookingItem?.bookedQty ??
    bookingItem?.count ??
    0
  );
};

const isEquipmentCheckedInForBooking = (booking, equipmentItem) => {
  if (!booking || !equipmentItem) return false;
  const checkedInItems = booking.checkedInItems || {};
  if (!checkedInItems || typeof checkedInItems !== 'object') return false;

  const targetIds = new Set([
    String(equipmentItem.id || '').trim(),
    String(equipmentItem.equipmentId || '').trim(),
    String(equipmentItem.itemId || '').trim()
  ].filter(Boolean));

  if (targetIds.size > 0) {
    for (const key of targetIds) {
      if (checkedInItems[key] === true) return true;
    }
  }

  // Fallback: if we cannot map by id but the booking has one matching item and any check-in flag is true.
  const matchingItems = Array.isArray(booking.items)
    ? booking.items.filter((item) => isMatchingEquipment(item, equipmentItem))
    : [];
  if (matchingItems.length === 1) {
    const matchingItem = matchingItems[0];
    const matchId = String(matchingItem?.equipmentId || matchingItem?.id || matchingItem?.itemId || '').trim();
    if (matchId && checkedInItems[matchId] === true) return true;
  }

  return false;
};

const bookingOverdueWithoutCheckin = (booking, equipmentItem, startDate) => {
  const bookingEnd = toDateOnly(booking?.endDate);
  const requestedStart = toDateOnly(startDate);
  const today = toDateOnly(new Date());
  if (!bookingEnd || !requestedStart || !today) return false;

  const isOverdue = today > bookingEnd;
  const requestAfterBookedEnd = requestedStart > bookingEnd;
  const checkedIn = isEquipmentCheckedInForBooking(booking, equipmentItem);

  return isOverdue && requestAfterBookedEnd && !checkedIn;
};

export const calculateEquipmentAvailability = ({
  equipmentItem,
  bookings,
  startDate,
  endDate,
  excludeBookingId = null
}) => {
  if (!equipmentItem) {
    return { availableQty: 0, bookedQty: 0, overlappingBookings: [] };
  }

  const totalQty = Number(
    equipmentItem.totalQty ??
    equipmentItem.totalQuantity ??
    equipmentItem.quantity ??
    equipmentItem.qty ??
    equipmentItem.availableQty ??
    0
  );

  const overlappingBookings = (bookings || []).filter((booking) => {
    if (!booking || booking.id === excludeBookingId) return false;
    if (!isStockHoldingStatus(booking.status)) return false;
    if (!Array.isArray(booking.items) || !booking.items.some((item) => isMatchingEquipment(item, equipmentItem))) {
      return false;
    }
    const overlapsRequestedRange = bookingOverlapsRange(booking, startDate, endDate);
    const overdueHoldActive = bookingOverdueWithoutCheckin(booking, equipmentItem, startDate);
    return overlapsRequestedRange || overdueHoldActive;
  });

  const bookedQty = overlappingBookings.reduce(
    (sum, booking) => sum + getBookedQtyForEquipment(booking, equipmentItem),
    0
  );

  return {
    availableQty: Math.max(0, totalQty - bookedQty),
    bookedQty,
    overlappingBookings,
    hasOverdueUnreturned: overlappingBookings.some((booking) =>
      bookingOverdueWithoutCheckin(booking, equipmentItem, startDate)
    )
  };
};

export const calculateMultiItemAvailability = ({
  selectedItems,
  equipmentById,
  bookings,
  startDate,
  endDate,
  excludeBookingId = null
}) => {
  const conflicts = [];

  (selectedItems || []).forEach((selectedItem) => {
    const equipmentItem = equipmentById[selectedItem.equipmentId];
    if (!equipmentItem) {
      conflicts.push({
        equipmentId: selectedItem.equipmentId,
        equipmentName: selectedItem.equipmentName || 'Unknown equipment',
        requestedQty: Number(selectedItem.quantity || 0),
        availableQty: 0
      });
      return;
    }

    const { availableQty } = calculateEquipmentAvailability({
      equipmentItem,
      bookings,
      startDate,
      endDate,
      excludeBookingId
    });

    const requestedQty = Number(selectedItem.quantity || 0);
    if (requestedQty > availableQty) {
      conflicts.push({
        equipmentId: selectedItem.equipmentId,
        equipmentName: selectedItem.equipmentName || equipmentItem.name,
        requestedQty,
        availableQty
      });
    }
  });

  return {
    isAvailable: conflicts.length === 0,
    conflicts
  };
};

